import { Diamond, LogoSaml } from "components/icons/generated";
import BadgeNext from "ds/components/BadgeNext";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tooltip from "ds/components/Tooltip";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import setLocation from "shared/setLocation";
import { BillingTierFeature, SamlSettings } from "types/generated";
import useTierFeature from "views/Account/hooks/useTierFeature";
import ActionCard from "ds/components/ActionCard";

import { openSingleSignOnDrawer } from "../components/Drawer";
import SettingsSingleSignOnCreateSAML from "./Create";
import SettingsSingleSignOnDetailsSAML from "./Details";
import SettingsSingleSignOnEditSAML from "./Edit";
import { showDisableConfirmation } from "./Disable";

type SettingsSingleSignOnSAMLProps = {
  integration: SamlSettings | undefined;
  oidcAlreadyEnabled: boolean;
};

const SettingsSingleSignOnSAML = ({
  integration,
  oidcAlreadyEnabled,
}: SettingsSingleSignOnSAMLProps) => {
  const isTierContainSAML = useTierFeature(BillingTierFeature.CustomSsoSaml);
  const isIntegrationActive = !!integration?.adminLogin;

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationSAML,
    defaultCallbackTrackProperties: {
      method: "SAML",
    },
  });

  const onPress = () => {
    openSingleSignOnDrawer({
      title: "Set up SAML SSO",
      children: ({ setIsDirty }) => <SettingsSingleSignOnCreateSAML setIsDirty={setIsDirty} />,
      isDismissable: false,
    });
  };

  const handleActivate = () => {
    setLocation("/saml/start");
    trackSegmentAnalyticsEvent("Activate Click");
  };

  const handleDisable = () => {
    showDisableConfirmation();
    trackSegmentAnalyticsEvent("Disable Click");
  };

  const handleShowDetails = () => {
    if (integration) {
      openSingleSignOnDrawer({
        title: "SAML SSO",
        children: () => <SettingsSingleSignOnDetailsSAML integration={integration} />,
      });
    }
  };

  const handleEditClaimMappings = () => {
    if (integration) {
      openSingleSignOnDrawer({
        title: "Edit SAML SSO",
        children: ({ setIsDirty }) => (
          <SettingsSingleSignOnEditSAML integration={integration} setIsDirty={setIsDirty} />
        ),
        isDismissable: true,
      });
    }
    trackSegmentAnalyticsEvent("Edit Click");
  };

  return (
    <ActionCard
      title="SAML"
      icon={LogoSaml}
      actionComponent={
        <Box gap="medium">
          {/* Regular "Set up" flow */}
          {isTierContainSAML && !integration && !oidcAlreadyEnabled && (
            <Button variant="secondary" onPress={onPress}>
              Set up
            </Button>
          )}

          {/* Disabled "Set up" flow because the SAML is no available in tier */}
          {!isTierContainSAML && (
            <Tooltip
              active
              on={(props) => (
                <Button variant="secondary" disabled {...props}>
                  Set up
                </Button>
              )}
            >
              Upgrade account to use SAML SSO 2.0
            </Tooltip>
          )}

          {/* Disabled "Set up" button because OIDC is already enabled */}
          {isTierContainSAML && !integration && oidcAlreadyEnabled && (
            <Tooltip
              active
              on={(props) => (
                <Button variant="secondary" disabled {...props}>
                  Set up
                </Button>
              )}
            >
              OIDC integration already present, can't set up SAML
            </Tooltip>
          )}

          {isTierContainSAML && integration && !isIntegrationActive && (
            <Button variant="primary" onPress={handleActivate}>
              Activate
            </Button>
          )}

          {isTierContainSAML && integration && (
            <DropdownMenuEllipsis tooltip="SAML actions" dotsSize="medium">
              <DropdownMenuItem onAction={handleShowDetails}>See details</DropdownMenuItem>
              <DropdownMenuItem onAction={handleEditClaimMappings}>Edit</DropdownMenuItem>
              <DropdownMenuItem onAction={handleDisable} danger>
                Unlink
              </DropdownMenuItem>
            </DropdownMenuEllipsis>
          )}
        </Box>
      }
      titleSuffix={
        <Box direction="row" gap="medium" align="center">
          {!isTierContainSAML && (
            <BadgeNext variant="purple" type="strong" startIcon={Diamond} iconOnly />
          )}
          {isTierContainSAML && integration && isIntegrationActive && (
            <BadgeNext variant="green" type="strong" text="Active" />
          )}
        </Box>
      }
      description="Connect your Spacelift account to a SAML 2.0 supporting identity provider."
    />
  );
};

export default SettingsSingleSignOnSAML;
